import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaExclamationTriangle } from "react-icons/fa";
import { FaPlus, FaXmark } from "react-icons/fa6";
import { Button } from "../../../../components/core/basic/Button";
import { Modal } from "../../../../components/core/basic/Modal";
import { HoverInfo } from "../../../../components/core/basic/Tooltip";
import Loading from "../../../../components/core/blocks/Loading";
import { DevErrorDisplay } from "../../../../components/core/blocks/DevErrorDisplay";
import { TextField } from "../../../../components/fields/base/TextField";
import { Flex, VStack } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";
import { AMC_CLIENT, COUNTRY_CODE_TO_MARKET_PLACE_ID_MAP, useMarketplaceIdLabel } from "../../utils/utils";
import { useAmcAdvertisers } from "../../api/useAmcAdvertisers";
import { useAdvertisingAccounts } from "../../../onboarding/amazon/useAdvertisingAccounts";
import { useOnboardingSummary } from "../../../onboarding/amazon/useOnboardingSummary";
import { type OnboardingSummary } from "../../../../zodios/apiDefinitions/api/onboardingSummary";
import { useAMCInstanceCreationRequest } from "../../api/useAMCInstanceCreationRequest";
import { useCreateAMCInstanceRequest } from "../../api/useCreateAMCInstanceRequest";
import { useUpdateAMCInstanceRequest } from "../../api/useUpdateAMCInstanceRequest";
import {
  AMC_ADVERTISER_TYPE,
  type AmcAdvertiserUpdate,
  type AmcInstanceCreationRequest,
  type AmcInstanceCreationRequestBody,
} from "../../../../zodios/apiDefinitions/amc";
import { errorToast, successToast } from "../../../../utils/toasts";
import { AmcEntityIdsPicker } from "./AmcEntityIdsPicker";
import { getRecommendedEntityIds } from "../../utils/amcEntityIds";

const DSP_ENTITY_ID_LENGTH = 18;

const noticeStyles = css({
  fontSize: "large",
  marginTop: "medium",
  marginBottom: "large",
  marginX: "auto",
  width: "75%",
  textAlign: "center",
});

/**
 * A DSP entity ID: a saved one, which can only be removed, or one the user is typing in. Keyed so its
 * field keeps its state when an earlier row is removed.
 */
type DspRow = { key: string; value: string; saved: boolean };

const isValidDspId = (id: string) => id.length === DSP_ENTITY_ID_LENGTH;

const sponsoredAdsAdvertiser = (id: string, marketplaceId: string | null | undefined): AmcAdvertiserUpdate => ({
  type: AMC_ADVERTISER_TYPE.sponsoredAds,
  id,
  marketplaceId: marketplaceId ?? undefined,
});

const displayAdvertiser = (id: string): AmcAdvertiserUpdate => ({
  type: AMC_ADVERTISER_TYPE.display,
  id,
  marketplaceId: null,
});

const selectProfileIds = (summary: OnboardingSummary) =>
  Object.values(summary.amazon_profile_ids).flatMap(({ id }) => (id ? [id] : []));

export function ManageAMCInstancesModal({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}) {
  const { t } = useTranslation(["amc"]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t(($) => $.amc["Manage AMC Instances"])}
      css={{ width: "60vw", maxHeight: "98vh", padding: "xlarge" }}
    >
      <Flex direction={"column"} overflowY={"auto"}>
        {/* The modal unmounts its content on close, which discards any unsaved changes. */}
        <ManageAMCInstances onClose={() => onOpenChange(false)} />
      </Flex>
    </Modal>
  );
}

function ManageAMCInstances({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(["common"]);
  const advertisers = useAmcAdvertisers();
  const requestQuery = useAMCInstanceCreationRequest();

  // Edits are made against the saved advertisers and the latest request, so both must be known first:
  // otherwise saved advertisers would read as removed, and an existing request as none.
  if (advertisers.isError || requestQuery.isError) {
    return (
      <DevErrorDisplay
        error={requestQuery.error ?? undefined}
        userMessage={t(($) => $.common["Something went wrong. Please try again."])}
      />
    );
  }
  if (advertisers.isLoading || requestQuery.isPending) {
    return <Loading fullPage={false} height={"250px"} />;
  }

  return <AMCInstanceEditor onClose={onClose} latestRequest={requestQuery.data} />;
}

function AMCInstanceEditor({
  onClose,
  latestRequest,
}: {
  onClose: () => void;
  /** `null` when the org has never requested an instance. */
  latestRequest: AmcInstanceCreationRequest | null;
}) {
  const { t } = useTranslation(["amc", "common"]);

  const accountsQuery = useAdvertisingAccounts();
  const profileIdsQuery = useOnboardingSummary({ select: selectProfileIds });
  const { instance, sponsoredAds: savedSponsoredAds, dsp: savedDsp, byId: savedById } = useAmcAdvertisers();
  const createRequest = useCreateAMCInstanceRequest();
  const updateRequest = useUpdateAMCInstanceRequest();
  const isSubmitting = createRequest.isPending || updateRequest.isPending;

  const savedEntityIds = useMemo(() => new Set(savedSponsoredAds.map(({ id }) => id)), [savedSponsoredAds]);
  const savedDspIds = savedDsp.map(({ id }) => id);

  const [draftEntityIds, setDraftEntityIds] = useState<ReadonlySet<string> | null>(null);
  const [draftDspRows, setDraftDspRows] = useState<DspRow[] | null>(null);
  const [showRemovalConfirmation, setShowRemovalConfirmation] = useState(false);

  const selectedEntityIds = draftEntityIds ?? savedEntityIds;
  const dspRows = draftDspRows ?? savedDspIds.map((id) => ({ key: `saved-${id}`, value: id, saved: true }));
  const dspIds = [...new Set(dspRows.map(({ value }) => value).filter(Boolean))];

  const isNew = latestRequest === null;
  const statuses = [latestRequest?.instance_creation_status, latestRequest?.advertiser_creation_status];
  const failed = statuses.includes("EXCEPTION");
  const pending = !isNew && !failed && statuses.some((status) => status !== "COMPLETED");

  const advertisersToRemove = [
    ...savedSponsoredAds
      .filter(({ id }) => !selectedEntityIds.has(id))
      .map(({ id, marketplaceId }) => sponsoredAdsAdvertiser(id, marketplaceId)),
    ...savedDspIds.filter((id) => !dspIds.includes(id)).map(displayAdvertiser),
  ];

  const hasChanges =
    advertisersToRemove.length > 0 ||
    [...selectedEntityIds].some((id) => !savedEntityIds.has(id)) ||
    dspIds.some((id) => !savedDspIds.includes(id));
  // An instance with no creation request on record has nothing to update through this endpoint.
  const canSubmit =
    hasChanges &&
    dspRows.every(({ value, saved }) => saved || !value || isValidDspId(value)) &&
    !(isNew && instance?.instance_id);

  // `mutateAsync`, so the toast still shows when the modal closes before the request settles.
  const submit = async () => {
    const accountMarketplaceIds = new Map<string, string>();
    for (const account of accountsQuery.data ?? []) {
      for (const { id, countryCode } of account.entityIds) {
        const marketplaceId = COUNTRY_CODE_TO_MARKET_PLACE_ID_MAP[countryCode];
        if (marketplaceId) {
          accountMarketplaceIds.set(id, marketplaceId);
        }
      }
    }
    // Every selected advertiser, saved ones included: the request replaces the instance's set.
    const advertisersToAdd = [
      ...[...selectedEntityIds].map((id) =>
        sponsoredAdsAdvertiser(id, accountMarketplaceIds.get(id) ?? savedById.get(id)?.marketplaceId),
      ),
      ...dspIds.map(displayAdvertiser),
    ];
    const body: AmcInstanceCreationRequestBody = {
      instance_creation_request: {},
      advertiser_creation_request: {
        advertisersToAdd,
        ...(advertisersToRemove.length > 0 && { advertisersToRemove }),
      },
      amc_client: AMC_CLIENT,
    };

    try {
      if (latestRequest) {
        await updateRequest.mutateAsync({ requestId: latestRequest.id, body });
      } else {
        await createRequest.mutateAsync(body);
      }
    } catch (error) {
      errorToast({
        message:
          isNew ?
            t(($) => $.amc["Error Requesting New AMC Instance"])
          : t(($) => $.amc["Error Sending AMC Instance Update Request"]),
        error,
      });
      return;
    }
    successToast({
      message:
        isNew ?
          t(($) => $.amc["Successfully Requested New AMC Instance"])
        : t(($) => $.amc["AMC Instance Update Request Successfully Sent"]),
    });
    onClose();
  };

  if (showRemovalConfirmation) {
    return (
      <RemovalConfirmation
        advertisers={advertisersToRemove}
        isSubmitting={isSubmitting}
        onBack={() => setShowRemovalConfirmation(false)}
        onConfirm={() => void submit()}
      />
    );
  }

  if (pending) {
    return <PendingNotice request={latestRequest} onClose={onClose} />;
  }

  return (
    <>
      <EditorIntro
        failed={failed}
        // Amazon's error details come from the advertiser step, as they always have; the instance step's
        // response is not shown.
        failureDetails={latestRequest?.advertiser_creation_raw.details}
        hasInstance={!!instance}
      />
      {!instance && (
        <Flex justifyContent={"center"} padding={"medium"}>
          <Button
            isDisabled={!profileIdsQuery.data || !accountsQuery.data}
            onPress={() => {
              setDraftEntityIds(getRecommendedEntityIds(profileIdsQuery.data ?? [], accountsQuery.data ?? []));
            }}
          >
            {t(($) => $.amc["Select Recommended Entity IDs"])}
          </Button>
        </Flex>
      )}
      <AmcEntityIdsPicker selectedEntityIds={selectedEntityIds} onSelectedEntityIdsChange={setDraftEntityIds} />
      <DspEntityIdsEditor rows={dspRows} onRowsChange={setDraftDspRows} />
      <Flex justifyContent={"center"} gap="xlarge">
        <Button variant="secondary" onPress={onClose}>
          {t(($) => $.common.actions.Cancel)}
        </Button>
        <Button
          onPress={() => {
            if (advertisersToRemove.length > 0) {
              setShowRemovalConfirmation(true);
            } else {
              void submit();
            }
          }}
          isDisabled={!canSubmit || isSubmitting}
        >
          {isNew ? t(($) => $.amc["Create Instance"]) : t(($) => $.amc["Update Instance"])}
        </Button>
      </Flex>
    </>
  );
}

function EditorIntro({
  failed,
  failureDetails,
  hasInstance,
}: {
  failed: boolean;
  failureDetails: string | undefined;
  hasInstance: boolean;
}) {
  const { t } = useTranslation(["amc"]);

  if (failed) {
    return (
      <>
        <p className={css({ textAlign: "center" })}>
          <FaExclamationTriangle />{" "}
          {t(($) => $.amc["There was an error creating the requested AMC instance. Please try again."])}
        </p>
        {failureDetails && (
          <p className={css({ paddingX: "large", fontSize: "small" })}>
            <b className={css({ marginRight: "small" })}>{t(($) => $.amc["Details:"])}</b>
            {failureDetails}
          </p>
        )}
      </>
    );
  }

  return (
    <p className={noticeStyles}>
      {hasInstance ?
        t(
          ($) =>
            $.amc[
              "To update the existing AMC instance associated with this account, select the additional advertising profiles or marketplaces you wish to add."
            ],
        )
      : t(
          ($) =>
            $.amc[
              "To create a new AMC instance, start by selecting your desired advertising profiles below. Once selected, you can choose your preferred marketplaces to include in the new instance."
            ],
        )
      }
    </p>
  );
}

function DspEntityIdsEditor({ rows, onRowsChange }: { rows: DspRow[]; onRowsChange: (rows: DspRow[]) => void }) {
  const { t } = useTranslation(["amc"]);
  const nextKey = useRef(0);

  return (
    <VStack gap="medium" css={{ justifyContent: "center", alignItems: "flex-start" }}>
      <h6 className={css({ color: "#73706E", fontWeight: "700", display: "flex", gap: "small" })}>
        {t(($) => $.amc["DSP Entity IDs (optional)"])}
        <HoverInfo>
          {t(
            ($) =>
              $.amc[
                "This {length}-digit ID can be found on the Amazon DSP Campaign Manager dashboard, under the Advertisers section. It is visible directly below the advertiser account name in the Advertiser column."
              ],
            { length: DSP_ENTITY_ID_LENGTH },
          )}
        </HoverInfo>
      </h6>

      {rows.map((row, index) => (
        <Flex key={row.key} gap="small" alignItems="flex-end">
          <TextField
            label={t(($) => $.amc["DSP Entity ID #{number}"], { number: index + 1 })}
            value={row.value}
            isDisabled={row.saved}
            placeholder={t(($) => $.amc["Enter DSP ID"])}
            onChange={(value) => onRowsChange(rows.map((r) => (r.key === row.key ? { ...r, value } : r)))}
            errorMessage={
              row.value && !isValidDspId(row.value) ?
                t(($) => $.amc["DSP Entity ID must be {length} digits"], { length: DSP_ENTITY_ID_LENGTH })
              : undefined
            }
            css={{ width: "400px" }}
          />
          <Button
            variant="contentOnly"
            aria-label={t(($) => $.amc["Remove DSP Entity ID #{number}"], { number: index + 1 })}
            onPress={() => onRowsChange(rows.filter(({ key }) => key !== row.key))}
          >
            <FaXmark />
          </Button>
        </Flex>
      ))}

      <Button
        onPress={() => onRowsChange([...rows, { key: `new-${nextKey.current++}`, value: "", saved: false }])}
        variant={"contentOnly"}
        isDisabled={rows.some(({ value, saved }) => !saved && !isValidDspId(value))}
      >
        <FaPlus /> {t(($) => $.amc["Add Another DSP ID"])}
      </Button>
    </VStack>
  );
}

function RemovalConfirmation({
  advertisers,
  isSubmitting,
  onBack,
  onConfirm,
}: {
  advertisers: AmcAdvertiserUpdate[];
  isSubmitting: boolean;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation(["amc"]);
  const marketplaceIdLabel = useMarketplaceIdLabel();

  return (
    <>
      <p className={noticeStyles}>
        <FaExclamationTriangle className={css({ marginRight: "small", color: "#c0392b" })} />
        {t(
          ($) =>
            $.amc[
              "You are about to remove the following advertisers from this AMC instance. Data associated with these advertisers will stop being ingested into the instance for reporting."
            ],
        )}
      </p>
      <VStack gap="small" css={{ alignItems: "flex-start", margin: "0 auto", width: "75%", padding: "medium" }}>
        {advertisers.map(({ type, id, marketplaceId }) => (
          <div key={`${type}-${id}`}>
            <b>
              <span>
                {type === AMC_ADVERTISER_TYPE.display ?
                  t(($) => $.amc["DSP Entity ID"])
                : t(($) => $.amc["Sponsored Ads Entity ID"])}
              </span>
              :
            </b>{" "}
            <span>{id}</span>
            {marketplaceId && <span>{` (${marketplaceIdLabel(marketplaceId)})`}</span>}
          </div>
        ))}
      </VStack>
      <Flex justifyContent={"center"} gap="xlarge" paddingTop="medium">
        <Button variant="secondary" onPress={onBack}>
          {t(($) => $.amc["Go Back"])}
        </Button>
        <Button onPress={onConfirm} isDisabled={isSubmitting}>
          {t(($) => $.amc["Confirm Removal"])}
        </Button>
      </Flex>
    </>
  );
}

function PendingNotice({ request, onClose }: { request: AmcInstanceCreationRequest; onClose: () => void }) {
  const { t } = useTranslation(["amc", "common"]);

  return (
    <>
      <p className={noticeStyles}>
        {t(
          ($) =>
            $.amc[
              "Changes to your AMC instance are currently pending. No additional updates can be made until they are complete. Please try again soon."
            ],
        )}
      </p>
      <p className={noticeStyles}>
        <b>{t(($) => $.amc["Current Instance Status:"])} </b> {request.instance_creation_status}
      </p>
      <p className={noticeStyles}>
        <b>{t(($) => $.amc["Current Advertiser Status:"])} </b> {request.advertiser_creation_status}
      </p>
      <Flex justifyContent={"center"} padding={"large"}>
        <Button onPress={onClose}>{t(($) => $.common.actions.Continue)}</Button>
      </Flex>
    </>
  );
}
